import React from "react";
import { Printer, Droplet, FileText, Flag, ArrowRight } from "lucide-react";

const STEPS = [
  {
    icon: Printer,
    label: "Printer System",
    sub: "Your wide-format setup",
    color: "#0F1F4D",
  },
  {
    icon: Droplet,
    label: "Ink",
    sub: "Matched to your model",
    color: "#1488DC",
  },
  {
    icon: FileText,
    label: "Paper",
    sub: "Bond · Satin · Glossy",
    color: "#1488DC",
  },
  {
    icon: Flag,
    label: "Vinyl / Banner",
    sub: "Indoor &amp; outdoor",
    color: "#0F1F4D",
  },
];

export default function SuppliesSystemFlow() {
  return (
    <section
      data-testid="supplies-system-flow-section"
      className="bg-white py-24 md:py-32 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="mb-16 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">
            One connected system
          </div>
          <h2
            data-testid="supplies-system-flow-headline"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-tight"
          >
            Your Printing System
          </h2>
        </div>

        {/* CMYK ribbon */}
        <div className="cmyk-ribbon h-1 w-24 mb-12 rounded-full" aria-hidden />

        {/* Flow */}
        <div className="relative">
          <div
            data-testid="supplies-system-flow"
            className="grid grid-cols-1 md:grid-cols-11 gap-4 md:gap-2 items-stretch"
          >
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const isLast = i === STEPS.length - 1;
              return (
                <React.Fragment key={step.label}>
                  <div
                    data-testid={`supplies-flow-step-${i}`}
                    className="md:col-span-2 group relative bg-[#F4F6F4] hover:bg-white border border-slate-200 hover:border-teal/40 rounded-2xl p-6 md:p-7 flex flex-col items-center text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                  >
                    <div className="cmyk-ribbon h-1 w-10 mb-5 rounded-full" aria-hidden />
                    <span
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                      style={{ backgroundColor: step.color }}
                    >
                      <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                    </span>
                    <h3 className="font-serif text-base md:text-lg font-bold text-navy leading-tight mb-1">
                      {step.label}
                    </h3>
                    <p
                      className="text-xs text-slate-500 leading-snug"
                      dangerouslySetInnerHTML={{ __html: step.sub }}
                    />
                  </div>

                  {!isLast && (
                    <div
                      aria-hidden
                      className="md:col-span-1 flex items-center justify-center py-2 md:py-0"
                    >
                      {/* Mobile: down arrow / Desktop: right arrow */}
                      <div className="md:hidden flex items-center gap-2 text-teal">
                        <span className="h-px w-12 bg-teal/30" />
                        <ArrowRight className="w-5 h-5 rotate-90" strokeWidth={2.5} />
                        <span className="h-px w-12 bg-teal/30" />
                      </div>
                      <div className="hidden md:flex items-center gap-1 text-teal w-full px-1">
                        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-teal/40 to-teal/40" />
                        <ArrowRight className="w-5 h-5 flex-shrink-0" strokeWidth={2.5} />
                        <span className="h-px flex-1 bg-gradient-to-r from-teal/40 via-teal/40 to-transparent" />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <p className="mt-12 text-base sm:text-lg text-slate-600 max-w-2xl">
          Printer → Ink → Paper → Vinyl/Banner. One connected supply chain, no
          missing pieces.
        </p>
      </div>
    </section>
  );
}
